/* vb-ring.js: wedding-ring PDP behaviour (template product.wedding-ring)
 *
 * TWO GALLERY RULES FROM THE HANDOFF. Do not undo them:
 *  1. No scroll-behavior:smooth and no animated scrollTo. `scroll-snap-type: x mandatory`
 *     re-targets animated scrolls mid-flight, so smooth navigation lands on the wrong
 *     slide and eventually locks the track. Instant scrollTo only.
 *  2. Never re-render the track to update indicators. Mutate dots and the active thumb
 *     ring directly. A re-render cancels the in-flight scroll and snaps it back.
 *  Also: no touch-action override on the track. `pan-y` kills the horizontal swipe.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- runes */
  /* Delegates to vb-runes.js (window.VBRunes), the same engine the rune translator and
     the blade PDP use. A private copy of the futhark maps used to live here; it could
     drift from the translator by construction, and the customer approves this preview
     before paying for a permanent engraving, so the two must never disagree. */
  function toRunes(input, futhark) {
    if (window.VBRunes && window.VBRunes.transliterate) {
      return window.VBRunes.transliterate(String(input || ''), futhark || 'elder');
    }
    return String(input || '').toUpperCase();   /* engine missing: plain text, never a wrong rune */
  }

  /* ---------------------------------------------------------------- sizes */
  // US size -> inside circumference in mm. Also drives the drawer calculator.
  var SIZES = [
    [3, 44.2], [3.5, 45.5], [4, 46.8], [4.5, 48.0], [5, 49.3], [5.5, 50.6],
    [6, 51.9], [6.5, 53.1], [7, 54.4], [7.5, 55.7], [8, 57.0], [8.5, 58.3],
    [9, 59.5], [9.5, 60.8], [10, 62.1], [10.5, 63.4], [11, 64.6], [11.5, 65.9],
    [12, 67.2], [12.5, 68.5], [13, 69.7], [13.5, 71.0], [14, 72.3], [15, 74.8]
  ];
  function mmForSize(us) {
    var n = parseFloat(String(us).replace(/[^\d.]/g, ''));
    for (var i = 0; i < SIZES.length; i++) if (SIZES[i][0] === n) return SIZES[i][1];
    return null;
  }
  function sizeForMm(mm) {
    /* Derived from SIZES, not hard-coded. The old 46-71 window rejected 72.5 mm while the
       conversion table printed beside it lists US 14 at 72.3 and US 15 at 74.8. */
    var LO = SIZES[0][1], HI = SIZES[SIZES.length - 1][1];
    if (!(mm >= LO - 1) || !(mm <= HI + 1)) return null;
    var best = null, diff = 1e9;
    SIZES.forEach(function (row) {
      var d = Math.abs(row[1] - mm);
      if (d < diff) { diff = d; best = row; }
    });
    return best;
  }

  /* ---------------------------------------------------------------- gallery */
  function Gallery(root) {
    var track = root.querySelector('[data-vbr-track]');
    if (!track) return;
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-vbr-dot]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-vbr-thumb]'));
    var index = 0;

    function mark(i) {
      if (i === index) return;
      index = i;
      dots.forEach(function (d, n) { d.setAttribute('aria-current', n === i ? 'true' : 'false'); });
      thumbs.forEach(function (t, n) { t.setAttribute('aria-current', n === i ? 'true' : 'false'); });
    }
    function go(i) {
      /* behavior:'instant' explicitly. Omitting it means 'auto', which defers to the
         element's computed scroll-behavior, so a stray `scroll-behavior: smooth` anywhere
         up the tree silently re-arms the snap bug this file exists to avoid.
         vb-blade.css already pairs mandatory snap with smooth, so it is live in this repo. */
      track.scrollTo({ left: i * track.clientWidth, behavior: 'instant' });
      mark(i);
    }
    thumbs.forEach(function (t, n) { t.addEventListener('click', function () { go(n); }); });
    dots.forEach(function (d, n) { d.addEventListener('click', function () { go(n); }); });

    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        var w = track.clientWidth || 1;
        mark(Math.round(track.scrollLeft / w));
      });
    }, { passive: true });
  }

  /* ---------------------------------------------------------------- PDP */
  function Ring(root) {
    var form = root.querySelector('[data-vb-ring-form]');
    if (!form) return;
    var cfg = {};
    try { cfg = JSON.parse(root.querySelector('[data-vbr-config]').textContent); }
    catch (e) { console.warn('[vb-ring] config JSON is invalid, falling back to defaults', e); }

    var state = {
      mode: 'single', width: 0, script: 'latin', tab: 0,
      engrave: false, text: ['', ''], date: ''
    };
    var widths = cfg.widths || [];
    var maxFor = function () {
      var w = widths[state.width];
      return (w && w.max) || cfg.engraving_max_chars || 32;
    };

    var $ = function (sel) { return form.querySelector(sel); };
    var $$ = function (sel) { return Array.prototype.slice.call(form.querySelectorAll(sel)); };

    /* ---- pair mode */
    $$('[data-vbr-pair]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.mode = btn.getAttribute('data-vbr-pair');
        /* Leaving tab at 1 after dropping to one band showed band two's text in the box
           while band one's text was what actually got submitted. */
        if (state.mode !== 'set') {
          state.tab = 0;
          state.text[1] = '';
          var e2 = $('[name="engraving_2"]'); if (e2) e2.value = '';
        }
        $$('[data-vbr-pair]').forEach(function (b) {
          b.setAttribute('aria-pressed', String(b === btn));
        });
        $('[name="pair_mode"]').value = state.mode;
        keepGalleryStill(render);
      });
    });

    /* ---- chip groups (width, script) */
    $$('[data-vbr-chipgroup]').forEach(function (group) {
      var name = group.getAttribute('data-vbr-chipgroup');
      group.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-vbr-chip]');
        if (!chip) return;
        Array.prototype.slice.call(group.querySelectorAll('[data-vbr-chip]')).forEach(function (c) {
          c.setAttribute('aria-pressed', String(c === chip));
        });
        var val = chip.getAttribute('data-vbr-chip');
        if (name === 'width') state.width = Number(val);
        if (name === 'script') { state.script = val; var f = $('[name="script"]'); if (f) f.value = val; }
        render();
      });
    });

    /* The sticky gallery is constrained by its grid cell, so when the buy box changes
       height the row grows or shrinks and the gallery's release point moves with it.
       Measured: opening the engraving panel moved the image by up to 326px. Cancel it by
       scrolling exactly as far as the element moved, so it stays put under the cursor. */
    function keepGalleryStill(mutate) {
      var g = root.querySelector('.vbr-gal');
      var before = g ? g.getBoundingClientRect().top : null;
      mutate();
      if (!g || before === null) return;
      /* Two passes: scrolling re-resolves the sticky offset, so at the exact point where
         the element goes from released to stuck a single correction leaves a residue
         (measured 60px at one scroll position). The second pass converges it. */
      var settle = function () {
        for (var pass = 0; pass < 3; pass++) {
          var delta = g.getBoundingClientRect().top - before;
          if (Math.abs(delta) <= 1) return true;
          window.scrollBy(0, delta);
        }
        return false;
      };
      /* Right at the point where the element flips between released and stuck, one
         correction can overshoot back across the boundary. Settle synchronously, then
         once more on the next frame after the browser has re-resolved sticky. */
      if (!settle()) requestAnimationFrame(settle);
    }

    /* ---- engraving toggle */
    var toggle = $('[data-vbr-engrave-toggle]');
    if (toggle) toggle.addEventListener('click', function () {
      keepGalleryStill(function () {
        state.engrave = !state.engrave;
        var cb = $('[name="engrave_on"]');
        if (cb) cb.checked = state.engrave;
        render();
      });
    });

    /* ---- band tabs */
    $$('[data-vbr-tab]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        state.tab = Number(tab.getAttribute('data-vbr-tab'));
        render();
      });
    });

    /* ---- engraving text */
    var input = $('[data-vbr-text]');
    if (input) input.addEventListener('input', function () {
      state.text[state.tab] = input.value;
      var f = $('[name="engraving_' + (state.tab + 1) + '"]');
      if (f) f.value = input.value;
      renderPreview();
    });

    $$('[data-vbr-sugg]').forEach(function (b) {
      b.addEventListener('click', function () {
        var v = b.getAttribute('data-vbr-sugg');
        state.text[state.tab] = v;
        if (input) input.value = v;
        var f = $('[name="engraving_' + (state.tab + 1) + '"]');
        if (f) f.value = v;
        renderPreview();
      });
    });

    /* ---- sizes */
    /* A CUSTOM variant has no mm value and no size we can act on, so it reveals a text
       field on that band. The size then travels as a line-item property instead of an
       order note the customer has to find and fulfilment has to remember to read. */
    function syncCustom() {
      $$('[data-vbr-size]').forEach(function (sel, i) {
        var row = $('[data-vbr-custom="' + (i + 1) + '"]');
        if (row) row.hidden = String(sel.value).toUpperCase().indexOf('CUSTOM') === -1;
      });
    }

    $$('[data-vbr-size]').forEach(function (sel) {
      sel.addEventListener('change', syncCustom);
      sel.addEventListener('change', function () {
        var out = sel.parentNode.querySelector('[data-vbr-mm]');
        var mm = mmForSize(sel.value);
        if (out) out.textContent = mm ? (mm.toFixed(1) + ' mm inside') : '';
        syncSticky();
      });
      sel.dispatchEvent(new Event('change'));
    });

    /* ---- wedding date */
    var dateInput = $('[name="wedding_date"]');
    if (dateInput) dateInput.addEventListener('change', function () {
      state.date = dateInput.value;
      renderDate();
    });

    /* ---- render ---------------------------------------------------- */
    function show(el, on) { if (el) el.hidden = !on; }

    function render() {
      var isSet = state.mode === 'set';
      show(form.querySelector('[data-vbr-size2row]'), isSet);
      show(form.querySelector('[data-vbr-tabs]'), isSet && state.engrave);
      show(form.querySelector('[data-vbr-engrave-body]'), state.engrave);
      var chk = form.querySelector('[data-vbr-check]');
      if (chk) chk.setAttribute('data-on', String(state.engrave));

      $$('[data-vbr-tab]').forEach(function (t) {
        t.setAttribute('aria-pressed', String(Number(t.getAttribute('data-vbr-tab')) === state.tab));
      });
      var max = maxFor();
      /* maxlength only constrains TYPING. Suggestion chips assign value directly, and
         narrowing the band after typing left an over-length string in place. Truncate on
         render, and expose the live limit so the submit handler can refuse it too. */
      if (state.text[state.tab] && state.text[state.tab].length > max) {
        state.text[state.tab] = state.text[state.tab].slice(0, max);
        var hf = $('[name="engraving_' + (state.tab + 1) + '"]');
        if (hf) hf.value = state.text[state.tab];
      }
      form.setAttribute('data-vb-max', String(max));
      if (input) input.value = state.text[state.tab] || '';
      if (input) input.setAttribute('maxlength', String(max));
      /* aria-expanded only: aria-pressed alongside it announced "toggle button, not
         pressed, collapsed", which is two states for one thing. */
      if (toggle) toggle.setAttribute('aria-expanded', String(state.engrave));

      /* Scoped to the SECTION, not the form. The price row with the compare-at and the
         SAVE badge sits above the <form>, so a form-scoped query matched nothing and the
         set price never revealed itself. */
      root.querySelectorAll('[data-vbr-setonly]').forEach(function (el) { el.hidden = !isSet; });

      renderPrice(isSet);
      renderPreview();
      renderDate();
      syncSticky();
    }

    /* "Switching to Set re-prices" (handoff, Interactions). Both the desktop price and the
       mobile sticky price, which are two separate nodes. */
    /* Show what the customer actually pays. The pair discount is automatic and certain to
       apply, so printing only the undiscounted total hid the whole saving: on the gold band
       that was $315 the buyer never saw before checkout. */
    function renderPrice(isSet) {
      var unit = Number(cfg.price_cents || 0);
      if (!unit) return;
      var pct = Number(cfg.set_discount_percent || 0);
      var full = isSet ? unit * 2 : unit;
      var net = (isSet && pct > 0) ? Math.round(full * (100 - pct) / 100) : full;

      root.querySelectorAll('[data-vbr-price]').forEach(function (el) { el.textContent = formatMoney(net); });
      var sticky = document.querySelector('[data-vbr-sticky-price]');
      if (sticky) sticky.textContent = formatMoney(net);

      /* The struck-through figure is the set total before the discount, which is the
         honest comparison. It is not compare_at_price and must not be presented as one. */
      var was = root.querySelector('[data-vbr-was]');
      if (was) {
        var show = isSet && net < full;
        was.hidden = !show;
        if (show) was.textContent = formatMoney(full);
      }
      var saved = root.querySelector('[data-vbr-saved]');
      if (saved) {
        var show2 = isSet && net < full;
        saved.hidden = !show2;
        if (show2) saved.textContent = 'You save ' + formatMoney(full - net);
      }
      /* Same number on the set card, so the card and the price row cannot disagree. */
      var cardP = root.querySelector('[data-vbr-pair="set"] .vbr-pair-p');
      if (cardP) cardP.textContent = formatMoney(net);
    }

    function formatMoney(cents) {
      if (window.Shopify && window.Shopify.formatMoney && cfg.money_format) {
        try { return window.Shopify.formatMoney(cents, cfg.money_format); } catch (e) {}
      }
      var s = (cents / 100).toFixed(2);
      return (cfg.currency_symbol || '$') + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function renderPreview() {
      var max = maxFor();
      var txt = state.text[state.tab] || '';
      var counter = form.querySelector('[data-vbr-count]');
      var hint = form.querySelector('[data-vbr-hint]');
      var over = txt.length > max;
      if (counter) { counter.textContent = txt.length + ' / ' + max; counter.classList.toggle('over', over); }
      /* The hint holds static advice about runes. Turning THAT red on over-length told the
         customer nothing; the counter is what is wrong. Swap the text as well. */
      if (hint) {
        hint.classList.toggle('over', over);
        hint.textContent = over
          ? 'Too long for this band width. Shorten it or choose a wider band.'
          : 'Runes cannot spell every English sound. Short Old Norse words work best.';
      }

      var band = form.querySelector('[data-vbr-band]');
      var stage = form.querySelector('.vbr-stage');
      if (band) {
        band.setAttribute('data-script', state.script === 'latin' ? 'latin' : 'runic');
        var span = band.querySelector('span');
        if (span) span.textContent = txt ? (state.script === 'latin' ? txt.toUpperCase() : toRunes(txt, state.script)) : '';
      }
      /* An empty band is a blank silver pill on a black panel: it reads as a broken
         element, not as a preview. Show the stage only once there is something to show. */
      if (stage) stage.hidden = !txt;
    }

    function renderDate() {
      var card = form.querySelector('[data-vbr-date]');
      if (!card) return;
      var head = card.querySelector('[data-vbr-date-h]');
      var body = card.querySelector('[data-vbr-date-b]');
      if (!state.date) {
        card.setAttribute('data-state', 'none');
        if (head) head.textContent = 'Wedding date';
        if (body) body.textContent = 'Add your date and we will tell you whether these arrive in time.';
        return;
      }
      /* Number(x || 6) yields NaN for a non-numeric metafield, which made spare NaN and
         fell through to "will not arrive in time". Number(x) || 6 does not. */
      var craft = Number(cfg.production_days) || 6;
      var days = craft + (state.engrave ? 3 : 0) + 6;
      /* Normalise to local midnight before differencing. Carrying the current time of day
         made spare come out one short at every hour except 00:00, so customers on time
         were told they were late. Round rather than floor so a DST shift cannot eat a day.
         Keep the 'T00:00:00' suffix: without it the date parses as UTC, not local. */
      var today = new Date(); today.setHours(0, 0, 0, 0);
      var ready = new Date(today); ready.setDate(ready.getDate() + days);
      var wedding = new Date(state.date + 'T00:00:00');
      var spare = Math.round((wedding - ready) / 86400000);
      var fmt = ready.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });

      if (spare >= 7) {
        card.setAttribute('data-state', 'good');
        if (head) head.textContent = 'Arrives with ' + spare + ' days to spare';
      } else if (spare >= 0) {
        card.setAttribute('data-state', 'tight');
        if (head) head.textContent = spare === 0 ? 'Arrives the day of' : 'Tight: ' + spare + ' days to spare';
      } else {
        card.setAttribute('data-state', 'late');
        if (head) head.textContent = 'Will not arrive in time';
      }
      if (body) {
        body.textContent = 'Estimated ready and delivered by ' + fmt + ', based on ' + craft +
          ' days to make' + (state.engrave ? ', 3 for engraving' : '') + ' and 6 in transit.';
      }
    }

    function syncSticky() {
      var p = document.querySelector('[data-vbr-sticky-meta]');
      if (!p) return;
      var bits = [];
      if (cfg.metal) bits.push(cfg.metal);
      bits.push(state.mode === 'set' ? 'Set' : 'One band');
      var s1 = $('[name="size_1"]'), s2 = $('[name="size_2"]');
      if (s1 && s1.value) bits.push('US ' + s1.value + (state.mode === 'set' && s2 && s2.value ? ' + US ' + s2.value : ''));
      p.textContent = bits.join(' · ');
    }

    syncCustom();
    render();
  }

  /* ---------------------------------------------------------------- sizer drawer */
  function Drawer() {
    var drawer = document.querySelector('[data-vbr-drawer]');
    var scrim = document.querySelector('[data-vbr-scrim]');
    if (!drawer || !scrim) {
      /* The drawer markup lives in the sizing section. If a merchant reorders it out of
         the template the buy-box link would still render and do nothing, silently. */
      document.querySelectorAll('[data-vbr-open-sizer]').forEach(function (b) { b.hidden = true; });
      return;
    }
    var last = null;

    function open() {
      last = document.activeElement;
      drawer.setAttribute('data-open', 'true');
      scrim.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
      var x = drawer.querySelector('[data-vbr-close]');
      if (x) x.focus();
    }
    function close() {
      drawer.setAttribute('data-open', 'false');
      scrim.setAttribute('data-open', 'false');
      document.body.style.overflow = '';
      if (last && last.focus) last.focus();
    }
    document.addEventListener('click', function (e) {
      if (!(e.target instanceof Element)) return;   // synthetic clicks can target Document
      if (e.target.closest('[data-vbr-open-sizer]')) { e.preventDefault(); open(); }
      else if (e.target.closest('[data-vbr-close]') || e.target === scrim) { close(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') close();
    });

    var mm = drawer.querySelector('[data-vbr-mm-input]');
    var out = drawer.querySelector('[data-vbr-mm-out]');
    if (mm && out) mm.addEventListener('input', function () {
      var v = parseFloat(mm.value);
      if (!v) { out.setAttribute('data-hit', 'false'); out.textContent = 'Enter the inside circumference in millimetres.'; return; }
      var hit = sizeForMm(v);
      if (!hit) {
        out.setAttribute('data-hit', 'false');
        out.textContent = v < 46
          ? 'That is smaller than the smallest size we make. Measure again around the widest part of the knuckle.'
          : 'That is larger than the largest size we make. Check you measured the circumference and not the diameter.';
        return;
      }
      out.setAttribute('data-hit', 'true');
      out.textContent = 'That is a US ' + hit[0] + ' (' + hit[1].toFixed(1) + ' mm).';
    });
  }

  /* FAQ is owned by sections/vb-faq.liquid, which renders native <details>. An accordion
     handler lived here binding [data-vbr-faq]/[data-vbr-q]; no markup in the theme emits
     either, so it was dead code. Removed rather than left to look like a working feature.
     The spec's "one open at a time" belongs on vb-faq via <details name="...">. */

  /* ---------------------------------------------------------------- boot */
  var booted = false;
  function init() {
    document.querySelectorAll('[data-vbr-root]').forEach(function (root) {
      if (root.__vbr) return;
      root.__vbr = true;
      Gallery(root);
      Ring(root);
    });
    /* shopify:section:load fires for ANY section in the editor. Re-running Drawer() added
       a second document click handler, and the two handlers raced on `last`: the second
       captured the close button the first had just focused, so closing returned focus into
       the closed drawer. */
    if (booted) return;
    booted = true;
    Drawer();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);

  window.VBRing = { toRunes: toRunes, sizeForMm: sizeForMm, mmForSize: mmForSize, SIZES: SIZES };
})();
